/**
 * Cambiar de empresa con otra pestaña abierta — `OD-0063` D-05 UNA PESTAÑA O
 * DOS. La razón y el canal, en `ui/otras-pestanas.ts`.
 *
 * ```text
 * useCambioDeEmpresa   el cambio de siempre (POST /me/organization), pero si
 *                      hay otra pestaña abierta se pide confirmar antes, y al
 *                      terminar se avisa a las demas
 * AvisoDeOtraPestana   lo que ve la OTRA pestaña: tapada entera, con
 *                      «Recargar», en vez de seguir pintando la empresa
 *                      anterior mientras sus peticiones van a la nueva
 * ```
 *
 * Lo usan el selector de empresa y el panel de la gestoría: son las dos vías
 * por las que la web cambia la empresa de la sesión.
 */
import { useEffect, useState, type ReactNode } from "react";
import { apiEmpresas } from "../api/cliente";
import { t } from "../i18n/es";
import { pestanasDelNavegador } from "../ui/otras-pestanas";
import { Aviso, Boton } from "../ui/primitivas";

/** Escrito como firma de llamada: `() => void` lo lee como texto el guardrail de JSX. */
type Antes = { (): void };

type Pendiente = { readonly id: string; readonly antes: Antes | undefined };

export function useCambioDeEmpresa(onCambiada: () => void, onSesionCaducada: () => void) {
  const [pendiente, setPendiente] = useState<Pendiente | null>(null);
  const [cambiando, setCambiando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function hacer(p: Pendiente): Promise<void> {
    setPendiente(null);
    setCambiando(true);
    const r = await apiEmpresas.cambiar(p.id);
    if (r.ok) {
      p.antes?.();
      pestanasDelNavegador().avisarCambio(p.id);
      onCambiada();
      return;
    }
    setCambiando(false);
    if (r.estado === 401) onSesionCaducada();
    else if (r.estado === 404) setError(t.selectorEmpresa.noDisponible);
    else if (r.estado === 0) setError(t.comun.sinRed);
    else setError(t.comun.errorGenerico);
  }

  /** `antes` se ejecuta cuando la API ya ha cambiado la sesión, justo antes de recargar. */
  async function cambiar(id: string, antes?: Antes): Promise<void> {
    setError(null);
    setCambiando(true);
    const otras = await pestanasDelNavegador().hayOtras();
    if (otras) {
      setCambiando(false);
      setPendiente({ id, antes });
      return;
    }
    await hacer({ id, antes });
  }

  const confirmacion: ReactNode =
    pendiente === null ? null : (
      <div className="confirmar-cambio">
        <Aviso tono="aviso" titulo={t.cambioDeEmpresa.otraPestanaTitulo}>
          {t.cambioDeEmpresa.otraPestanaTexto}
        </Aviso>
        <div className="acciones-en-linea">
          <Boton variante="secundario" onClick={() => void hacer(pendiente)}>
            {t.cambioDeEmpresa.cambiarIgualmente}
          </Boton>
          <Boton variante="terciario" onClick={() => setPendiente(null)}>
            {t.cambioDeEmpresa.noCambiar}
          </Boton>
        </div>
      </div>
    );

  return { cambiar, cambiando, error, confirmacion } as const;
}

/** La empresa de la sesión ha cambiado en otra pestaña: esta ya no enseña la buena. */
export function AvisoDeOtraPestana({ organizationId }: { organizationId: string }) {
  const [otra, setOtra] = useState(false);
  useEffect(() => pestanasDelNavegador().alCambiar((id) => setOtra(id !== organizationId)), [organizationId]);
  if (!otra) return null;
  return (
    <div className="velo-otra-pestana" role="alertdialog" aria-modal="true" aria-labelledby="otra-pestana-titulo">
      <div className="velo-otra-pestana-caja">
        <h2 id="otra-pestana-titulo">{t.cambioDeEmpresa.cambiadaTitulo}</h2>
        <p>{t.cambioDeEmpresa.cambiadaTexto}</p>
        <Boton onClick={() => window.location.assign("/")}>{t.cambioDeEmpresa.recargar}</Boton>
      </div>
    </div>
  );
}
